import {useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {JobPostingRequest} from '../interface/JobPostingRequest';
import {addJobPosting, cancelJobPosting, getQuestionList} from '../api/jobApi';
import {checkAndSaveSenior, getSeniorDetail, getSeniorList, updateSenior} from '../api/seniorApi';
import {SeniorFormData} from '../interface/SeniorFormData';
import {defaultAlert, defaultConfirm} from '../utils/alert';
import {useFormValidation} from './useFormValidation';


interface JobQuestion {
    questionCode: string;
    questionName: string;
    questionType: 'O' | 'T' | 'M';
    required?: boolean;
    options?: { optionName: string }[];
}

export const useJobPosting = (serviceType: string) => {
        const navigate = useNavigate();
        const location = useLocation();
        const {validateJobApplyFirstPage, validateJobApplySecondPage} = useFormValidation();

        const reRegisterData = (location.state as any)?.reRegisterData;

        const [step, setStep] = useState(1);
        const [questionList, setQuestionList] = useState<JobQuestion[]>([]);
        const [seniors, setSeniors] = useState<SeniorFormData[]>([]);
        const [answers, setAnswers] = useState<{ [key: string]: string | { selection: string; text: string } }>({});
        const [isModalOpen, setIsModalOpen] = useState(false);
        const [originalJobPostingCode, setOriginalJobPostingCode] = useState<string | null>(null);

        const [form, setForm] = useState<JobPostingRequest>({
            elderName: '',
            gender: '',
            birthday: '',
            weight: '',
            height: '',
            careHours: '',
            longTermCareInsuranceNumber: '',
            relationship: '',
            serviceType: serviceType,
            workType: 'DAILY',
            workStartTime: '',
            workEndTime: '',
            careStartDate: '',
            careEndDate: '',
            carePlaceType: 'HOME',
            hospitalName: '',
            address: '',
            addressDetail: '',
            payNegotiable: 'Y',
            dailyPay: '',
            answers: []
        });

        // 재등록 데이터 세팅
        useEffect(() => {
            if (reRegisterData) {
                setOriginalJobPostingCode(reRegisterData.jobPostingCode);
                setForm(prev => ({
                    ...prev,
                    elderName: reRegisterData.patientName || '',
                    gender: reRegisterData.gender || '',
                    birthday: reRegisterData.birthday || '',
                    weight: reRegisterData.weight ? String(reRegisterData.weight) : '',
                    height: reRegisterData.height ? String(reRegisterData.height) : '',
                    careHours: reRegisterData.dailyHours ? String(reRegisterData.dailyHours) : '',
                    longTermCareInsuranceNumber: reRegisterData.longTermCareInsuranceNumber || '',
                    relationship: reRegisterData.relationship || '',
                    serviceType: reRegisterData.serviceType || serviceType,
                    workType: reRegisterData.workType || 'DAILY',
                    workStartTime: reRegisterData.workStartTime || '',
                    workEndTime: reRegisterData.workEndTime || '',
                    careStartDate: reRegisterData.careStartDate || '',
                    careEndDate: reRegisterData.careEndDate || '',
                    carePlaceType: reRegisterData.carePlaceType || 'HOME',
                    hospitalName: reRegisterData.hospitalName || '',
                    address: reRegisterData.address || '',
                    addressDetail: reRegisterData.addressDetail || '',
                    payNegotiable: reRegisterData.negotiable === '협의가능' ? 'Y' : 'N',
                    dailyPay: reRegisterData.dailyPay ? String(reRegisterData.dailyPay) : '',
                    seniorCode: reRegisterData.seniorCode || undefined,
                }));

                // 기존 답변 세팅
                if (reRegisterData.answers && Array.isArray(reRegisterData.answers)) {
                    const answersMap: { [key: string]: string | { selection: string; text: string } } = {};
                    reRegisterData.answers.forEach((a: any) => {
                        try {
                            const parsed = JSON.parse(a.answerText);
                            if (parsed.selection) {
                                answersMap[a.questionCode] = parsed;
                            } else {
                                answersMap[a.questionCode] = a.answerText;
                            }
                        } catch {
                            answersMap[a.questionCode] = a.answerText;
                        }
                    });
                    setAnswers(answersMap);
                }
            }
        }, []);

        // 어르신 목록 조회
        const fetchSeniors = async () => {
            try {
                const res = await getSeniorList();
                setSeniors(res.data.data);
            } catch (error) {
                console.error('어르신 목록 조회 실패:', error);
                defaultAlert('어르신 목록을 불러오는 중 오류가 발생했습니다.');
            }
        };

        // 어르신 정보 조회
        const handleSeniorChange = async (code: string) => {
            setForm(prev => ({...prev, seniorCode: code}));

            try {
                const res = await getSeniorDetail(code);
                const data = res.data.data;
                setForm(prev => ({
                    ...prev,
                    elderName: data.name,
                    gender: data.gender,
                    birthday: data.birthday,
                    weight: data.weight,
                    height: data.height,
                    longTermCareInsuranceNumber: data.longTermCareInsuranceNumber,
                    relationship: data.relationship,
                }));
            } catch (error) {
                console.error('어르신 정보 조회 실패:', error);
                defaultAlert('어르신 정보를 불러오는 중 오류가 발생했습니다.');
            }
        };

        // Step 1 -> Step 2
        const onNextStep = async () => {
            if (!validateJobApplyFirstPage(form)) {
                return;
            }

            // 어르신 이름으로 기존 데이터와 비교
            const proceedToStep2 = async (seniorCodeToUse?: string) => {
                try {
                    const res = await getQuestionList();
                    const questionsWithRequired = res.data.map((q: JobQuestion) => ({
                        ...q,
                        required: true
                    }));
                    setQuestionList(questionsWithRequired);
                    if (seniorCodeToUse) {
                        setForm(prev => ({...prev, seniorCode: seniorCodeToUse}));
                    }
                    setStep(2);
                } catch (e) {
                    console.error(e);
                    defaultAlert('질문 목록을 불러오는 중 오류가 발생했습니다.');
                }
            };

            const formData = {
                name: form.elderName,
                birthday: form.birthday,
                gender: form.gender,
                weight: form.weight,
                height: form.height,
                longTermCareInsuranceNumber: form.longTermCareInsuranceNumber,
                relationship: form.relationship,
            };

            // 이름으로 기존 어르신 찾기 (seniors 목록 사용)
            const matchedSenior = (seniors || []).find(s => s.name === form.elderName);

            if (matchedSenior && matchedSenior.seniorCode) {
                // DB에서 상세 정보 조회
                try {
                    const detailRes = await getSeniorDetail(matchedSenior.seniorCode);
                    const dbData = detailRes.data.data;

                    const isDifferent =
                        dbData.birthday !== formData.birthday ||
                        dbData.gender !== formData.gender ||
                        dbData.weight !== formData.weight ||
                        dbData.height !== formData.height ||
                        dbData.longTermCareInsuranceNumber !== formData.longTermCareInsuranceNumber ||
                        dbData.relationship !== formData.relationship;

                    if (isDifferent) {
                        defaultConfirm(
                            '어르신 정보를 업데이트 하시겠습니까?',
                            '업데이트',
                            '닫기',
                            async (isConfirmed) => {
                                if (isConfirmed) {
                                    try {
                                        await updateSenior(matchedSenior.seniorCode!, formData);
                                    } catch (e) {
                                        console.error(e);
                                        defaultAlert('어르신 정보 업데이트 중 오류가 발생했습니다.');
                                        return;
                                    }
                                }
                                await proceedToStep2(matchedSenior.seniorCode);
                            }
                        );
                        return;
                    } else {
                        // 동일한 데이터면 그냥 진행
                        await proceedToStep2(matchedSenior.seniorCode);
                        return;
                    }
                } catch (e) {
                    console.error(e);
                    defaultAlert('어르신 정보를 불러오는 중 오류가 발생했습니다.');
                    return;
                }
            }

            // 이름으로 찾은 어르신이 없으면 무조건 새로 저장
            try {
                const res = await checkAndSaveSenior(formData);
                await proceedToStep2(res.data.data);
            } catch (e) {
                console.error(e);
                defaultAlert('어르신 정보 저장 중 오류가 발생했습니다.');
            }
        };

        // 답변 변경
    const handleAnswerChange = (questionCode: string, value: string | { selection: string; text: string }) => {
        setAnswers(prev => ({
            ...prev,
            [questionCode]: value
        }));
    };

        // 모달 열기/닫기
        const handleOpenModal = () => {

            if (!validateJobApplySecondPage(form, questionList, answers)) {
                return;
            }
            setIsModalOpen(true);
        };
        const handleCloseModal = () => setIsModalOpen(false);

        // 공고 등록 (재등록 시 기존 공고 취소 후 새 공고 등록)
        const handleConfirmSubmit = async () => {
            try {
                const answersArray = Object.entries(answers).map(([questionCode, answerValue]) => ({
                    questionCode,
                    answerText: typeof answerValue === 'string'
                        ? answerValue
                        : JSON.stringify(answerValue) // 복합식은 JSON 문자열로 변환
                }));

                const submitData = {
                    ...form,
                    answers: answersArray
                };

                // 재등록인 경우 기존 공고 취소 후 등록
                if (originalJobPostingCode) {
                    try {
                        await cancelJobPosting(originalJobPostingCode);
                    } catch (cancelError) {
                        console.error('기존 공고 취소 실패:', cancelError);
                        defaultAlert('기존 공고 취소에 실패했습니다.');
                        return;
                    }
                }

                await addJobPosting(submitData);
                setIsModalOpen(false);
                navigate('/announcement/matching');
            } catch (e) {
                console.error(e);
                defaultAlert('공고 등록에 실패했습니다.');
            }
        };

        return {
            step,
            setStep,
            form,
            setForm,
            questionList,
            seniors,
            answers,
            isModalOpen,
            isReRegister: !!originalJobPostingCode,
            fetchSeniors,
            handleSeniorChange,
            onNextStep,
            handleAnswerChange,
            handleOpenModal,
            handleCloseModal,
            handleConfirmSubmit,
        };
    }
;
